import merge from 'lodash/merge';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import type { I18nProvider } from 'react-admin';
import {
    AppLocale,
    DEFAULT_LOCALE,
    LOCALES,
    applyAppLocale,
    normalizeLocale,
    persistLocale,
    resolveInitialLocale,
} from './locale';
import es from './messages/es';
import ptBr from './messages/pt-br';

/*
 * Como a tradução funciona:
 *
 * - Chave nomeada (`ra.*`, `tooltips.*`): definida em messages/pt-br.ts e em messages/es/.
 *   Faltou em espanhol → mostra o texto pt-br (o merge abaixo usa pt-br como base).
 * - Todo o resto usa o PRÓPRIO TEXTO EM PORTUGUÊS como chave. Os componentes do
 *   react-admin já chamam translate(texto, { _: texto }) em label, title, primaryText,
 *   helperText, label de botão, choices e notify; e o texto que o backend devolve em
 *   `message` chega na notificação do mesmo jeito. Em pt-br nada precisa ser cadastrado;
 *   em espanhol a tradução vem de messages/es/catalog.ts. Faltou lá → mostra o português.
 * - Texto solto em JSX ou em componente MUI não passa pelo react-admin: envolva com
 *   translate('Texto em português') (useTranslate). Texto com variável:
 *   translate('Olá %{nome}', { nome }).
 */

const esWithFallback = merge({}, ptBr, es);

const getMessages = (locale: string) => (normalizeLocale(locale) === 'es' ? esWithFallback : ptBr);

const initialLocale = resolveInitialLocale();
applyAppLocale(initialLocale);

// allowMissing: chave sem tradução devolve a própria chave, já interpolada.
// Precisa ser a forma de 4 argumentos: com 3, o changeLocale descarta estas opções
// e getLocales() devolve [{ locale: 'en' }].
const baseProvider = polyglotI18nProvider(getMessages, initialLocale, LOCALES, { allowMissing: true });

const flatten = (tree: Record<string, any>, prefix = '', acc: Record<string, string> = {}) => {
    Object.keys(tree).forEach(key => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (tree[key] && typeof tree[key] === 'object') flatten(tree[key], path, acc);
        else acc[path] = tree[key];
    });
    return acc;
};

const ptFlat = new Map(Object.entries(flatten(ptBr)));
const esFlat = flatten(es);
const esKeys = new Set(Object.keys(esFlat));
const esValues = new Set(Object.values(esFlat));

// Textos pedidos em espanhol que caíram no fallback em português. Pra montar o lote
// de tradução de uma tela: abra o painel em espanhol, navegue, e rode no console
// `copy(JSON.stringify(pxI18nMissing(), null, 2))`. Complementa `yarn i18n:extract`,
// que acha os textos no código; este acha os que aparecem de fato (backend incluso).
const missing = new Map<string, string>();

declare global {
    interface Window {
        pxI18nMissing?: () => Record<string, string>;
    }
}

window.pxI18nMissing = () => Object.fromEntries(missing);

const trackMissing = (key: string, options?: any) => {
    if (baseProvider.getLocale() === DEFAULT_LOCALE) return;
    if (typeof key !== 'string' || esKeys.has(key) || esValues.has(key) || missing.has(key)) return;
    if (!/\p{L}/u.test(key)) return;
    missing.set(key, typeof options?._ === 'string' ? options._ : ptFlat.get(key) ?? key);
};

const i18nProvider: I18nProvider = {
    ...baseProvider,
    translate: (key, options) => {
        trackMissing(key, options);
        return baseProvider.translate(key, options);
    },
    // O react-admin só chama changeLocale quando o usuário troca no seletor.
    // Normaliza antes: getLocale() tem que devolver sempre um código de LOCALES, senão o
    // LocalesMenuButton (comparação exata) fica sem rótulo.
    changeLocale: locale => {
        const appLocale: AppLocale = normalizeLocale(locale) ?? DEFAULT_LOCALE;
        return Promise.resolve(baseProvider.changeLocale(appLocale)).then(() => {
            persistLocale(appLocale);
            applyAppLocale(appLocale);
        });
    },
};

export default i18nProvider;
